import { default as Modal } from 'antd/es/modal';
import { useEffect, useState } from 'react';

import { useOpenNotification } from 'utils/antd/notification-toasts';
import { logEventToHotjar } from 'utils/logging';
import { removeURLSearchParams, updateHistoryState } from 'utils/url';

import { useDeleteDisbursementPayoutDraftsId } from 'api';

import { useOrganisationId } from 'hooks/context';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { SecondaryButton } from '@atoms/buttons/SecondaryButton';

import { Content } from './Content';
import { DraftSentForApprovalDialog } from './steps/DraftSentForApprovalDialog';

import { useLocalState } from '../localState';

type Props = {
  open: boolean;
  onClose: () => void;
  onFinish?: () => void;
};

export const CreateManualPayoutDialog = ({
  open,
  onClose,
  onFinish
}: Props) => {
  const [draftDialogState, setDraftDialogState] = useState<{
    open: boolean;
    amount: string;
    currency: string;
    reference: string;
  } | null>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);

  const { updateKey } = useLocalState();
  const openNotification = useOpenNotification();
  const organisationId = useOrganisationId<number>();
  const [draftId, setDraftId] = useState<number | null>(null);

  const { mutate: deleteDraft } = useDeleteDisbursementPayoutDraftsId({
    mutation: {
      onSuccess: () => {
        openNotification({ kind: 'info', message: 'Draft has been deleted' });
      },
      onError: () => {}
    }
  });

  useEffect(() => {
    logEventToHotjar('create-manual-payout:dialog-open');
  }, []);

  const onFinishHandler = () => {
    updateKey(Date.now());
    setDraftDialogState(null);
    onClose();
    onFinish?.();
  };

  const onCloseHandler = () => {
    const url = removeURLSearchParams('createManualPayout');
    updateHistoryState(url);

    if (organisationId && draftId) {
      deleteDraft({ organisationId, id: draftId });
    }

    onClose();
  };

  // Instead of Modal.confirm, open our own confirmation modal
  const handleCancel = () => {
    setConfirmOpen(true);
  };

  return (
    <>
      {/* Draft modal opened from the create-manual-payout modal in case of approval flow */}
      {draftDialogState?.open && draftId && (
        <DraftSentForApprovalDialog
          amount={draftDialogState.amount}
          currency={draftDialogState.currency}
          reference={draftDialogState.reference}
          draftId={draftId}
          onClose={onFinishHandler}
        />
      )}

      {/* Main dialog */}
      <Modal
        title="Create manual payout"
        open={open}
        onCancel={handleCancel}
        footer={null}
        maskClosable={false}
        keyboard={false}
      >
        <Content
          onFinish={onFinishHandler}
          onCancel={handleCancel} // keep same UX path: cancel -> confirm modal
          onDraftCreated={(id) => setDraftId(id)}
          onShowApprovalDialog={(params) =>
            setDraftDialogState({ open: true, ...params })
          }
        />
      </Modal>

      {/* Confirmation dialog with custom footer */}
      <Modal
        title="Exit unfinished manual payout request"
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        footer={
          <div className="flex justify-end gap-2">
            <SecondaryButton onClick={() => setConfirmOpen(false)}>
              No
            </SecondaryButton>
            <PrimaryButton
              danger
              onClick={() => {
                setConfirmOpen(false);
                onCloseHandler(); // preserves original delete & URL logic
              }}
            >
              Yes
            </PrimaryButton>
          </div>
        }
        maskClosable={false}
        keyboard={false}
        destroyOnHidden
      >
        <p>Are you sure? It will be deleted.</p>
      </Modal>
    </>
  );
};
